import { HelpAudience } from './types';

/**
 * A Funkciók oldal katalógusa: minden, amit a szoftver tud, a felhasználó nyelvén.
 *
 * Új funkciónál ide kerül egy bejegyzés (`since` = a kiadás napja), és a
 * releases.ts-be egy újdonság-sor - lásd a help-docs skillt. A sorrend a menü
 * sorrendjét követi, modulon belül a fontosság szerint.
 */
export interface Feature {
  /** Egyedi, stabil azonosító - ez a horgony is a Funkciók oldalon (/features#id). */
  id: string;
  title: string;
  /** Mit lehet vele csinálni - egy-két mondat, a felhasználó szemszögéből. */
  description: string;
  /** A help/modules.ts modulja: innen öröklődik, ki látja, és a csoportosítás is. */
  module: string;
  /** Hová visz a „Megnyitás” gomb az appon belül. */
  to: string;
  /** Ha szűkebb körnek szól, mint a modul (pl. a modult mindenki látja, de ezt csak az admin). */
  audience?: HelpAudience;
  /** Mióta érhető el (ÉÉÉÉ-HH-NN) - az „Új” jelzés ebből számol. */
  since: string;
}

export const features: Feature[] = [
  // Szülők és játékosok appja
  {
    id: 'csaladi-felulet',
    title: 'Saját app a szülőknek és játékosoknak',
    description: 'Telefonra szabott felület négy füllel (Kezdőlap, Program, Hírek, Én): csak az, ami a gyereked programjához kell.',
    module: 'family-home',
    to: '/',
    since: '2026-10-09',
  },
  {
    id: 'csaladi-program',
    title: 'Program egy listában',
    description: 'Edzések, meccsek és események napok szerint, gyerekenkénti visszajelzéssel; meccsnél az eredmény is.',
    module: 'family-program',
    to: '/program',
    since: '2026-10-09',
  },
  {
    id: 'gyerek-adatlap',
    title: 'A gyerek adatlapja',
    description: 'Hiányzás bejelentése, elérhetőség és egészségügyi adatok gyerekenként, egy helyen.',
    module: 'family-me',
    to: '/me',
    since: '2026-10-09',
  },
  // Áttekintő és általános
  {
    id: 'attekinto',
    title: 'Szerepkörre szabott kezdőlap',
    description: 'A mai és közelgő események, lejáró sportorvosik, tagdíj hátralék, új jelentkezők - mindenki azt látja, ami neki fontos.',
    module: 'dashboard',
    to: '/',
    since: '2026-04-21',
  },
  {
    id: 'globalis-kereses',
    title: 'Keresés mindenben',
    description: 'Játékosok és események egy helyen kereshetők a felső sávból vagy a ⌘K / Ctrl+K billentyűkkel.',
    module: 'dashboard',
    to: '/',
    since: '2026-04-21',
  },
  {
    id: 'tanulo-mod',
    title: 'Tanuló mód és Súgó',
    description: 'Bekapcsolva minden oldal tetején rövid útmutató és bemutató animáció jelenik meg; a Súgóban minden modul leírása megtalálható.',
    module: 'dashboard',
    to: '/help',
    since: '2026-10-05',
  },
  {
    id: 'funkciok-ujdonsagok',
    title: 'Funkciók és újdonságok',
    description: 'Minden funkció egy helyen, linkkel oda, ahol használható; az újdonságokról pötty és a kezdőlapon egy sáv jelez.',
    module: 'dashboard',
    to: '/features',
    since: '2026-10-06',
  },

  // Naptár és események
  {
    id: 'naptar-nezetek',
    title: 'Klubnaptár több nézetben',
    description: 'Az összes edzés, mérkőzés és program havi, heti, napi vagy lista nézetben.',
    module: 'calendar',
    to: '/calendar',
    since: '2026-01-29',
  },
  {
    id: 'ical-feed',
    title: 'Naptár a telefonodon',
    description: 'Egy linkkel a Google vagy Apple naptáradba húzhatod az eseményeket, és maguktól frissülnek.',
    module: 'calendar',
    to: '/profile',
    since: '2026-10-05',
  },
  {
    id: 'visszajelzes',
    title: 'Visszajelzés eseményekre',
    description: 'Egy gombnyomással jelezhető, hogy ott leszel-e; az edző egy pillantással látja, ki jön és ki nem.',
    module: 'events',
    to: '/events',
    since: '2026-01-29',
  },
  {
    id: 'emlekeztetok',
    title: 'Értesítések és emlékeztetők',
    description: 'Új eseményről és a kezdés előtt push értesítést kapnak az érintettek; az edző a nem válaszolóknak külön is szólhat.',
    module: 'events',
    to: '/events',
    since: '2026-09-28',
  },
  {
    id: 'ismetlodo-esemenyek',
    title: 'Ismétlődő események',
    description: 'A heti edzéseket egyszer kell felvenni: a rendszer a megadott végdátumig ismétli őket.',
    module: 'events',
    to: '/events',
    audience: 'staff',
    since: '2026-01-29',
  },
  {
    id: 'palyaresz-foglalas',
    title: 'Pályarész foglalás',
    description: 'Eseménynél kijelölhető, a pálya melyik részét használja a csapat - így két csapat is edzhet egyszerre ütközés nélkül.',
    module: 'events',
    to: '/events',
    audience: 'staff',
    since: '2026-07-28',
  },
  {
    id: 'hozzaszolasok',
    title: 'Hozzászólások eseményekhez',
    description: 'Kérdések, tudnivalók közvetlenül az eseménynél, a hozzászólások száma a kártyán is látszik.',
    module: 'events',
    to: '/events',
    since: '2026-07-28',
  },
  {
    id: 'kikero',
    title: 'Iskolai kikérő',
    description: 'Eseményhez kikérő kérhető a szülőktől, a stáb pedig egy kattintással legenerálja a kikérőket.',
    module: 'events',
    to: '/events',
    since: '2026-10-02',
  },
  {
    id: 'vendegcsapat-esemeny',
    title: 'Esemény külsős csapatnak',
    description: 'Korosztály helyett vendégcsapat is megadható, így a bérlők és külsős csapatok is a klubnaptárban szerepelnek.',
    module: 'events',
    to: '/events',
    audience: 'staff',
    since: '2026-09-05',
  },

  // Mérkőzések
  {
    id: 'merkozes-eredmenyek',
    title: 'Mérkőzések és eredmények',
    description: 'Mérkőzés típusú eseménynél a mérkőzés magától létrejön; az eredmény és az ellenfél a listában színkódolva látszik.',
    module: 'matches',
    to: '/matches',
    since: '2026-07-28',
  },
  {
    id: 'felallas-szerkeszto',
    title: 'Felállás szerkesztő',
    description: 'Formáció választás, a játékosok pályára húzása, cserék a kispadra - a keret egy listából válogatható.',
    module: 'matches',
    to: '/matches',
    audience: 'staff',
    since: '2026-10-01',
  },
  {
    id: 'mentett-felallasok',
    title: 'Mentett felállások',
    description: 'A bevált felállás elmenthető, és a következő meccsnél egy kattintással betölthető.',
    module: 'matches',
    to: '/matches',
    audience: 'staff',
    since: '2026-10-05',
  },
  {
    id: 'futsal-merkozes',
    title: 'Futsal mérkőzés',
    description: 'A mérkőzés futsalnak jelölhető: a felállás szerkesztő 4+1 fővel nyílik, és a listában is látszik a „Futsal” jelzés.',
    module: 'matches',
    to: '/matches',
    since: '2026-10-09',
  },
  {
    id: 'mezszam-javaslat',
    title: 'Mezszám javaslat',
    description: 'A rendszer a korábbi meccsek alapján javasolja, ki milyen mezszámban szokott játszani.',
    module: 'matches',
    to: '/matches',
    audience: 'staff',
    since: '2026-10-05',
  },
  {
    id: 'merkozes-statisztika',
    title: 'Játékos statisztika',
    description: 'Meccsek, percek, gólok, gólpasszok és lapok játékosonként, korosztályra és időszakra szűrve, rendezhetően.',
    module: 'statistics',
    to: '/statistics',
    since: '2026-03-19',
  },

  // Edzések
  {
    id: 'jelenlet',
    title: 'Edzés jelenlét',
    description: 'Az edzés napjától rögzíthető, ki volt ott - a visszajelzésekkel összevetve.',
    module: 'trainings',
    to: '/events',
    audience: 'staff',
    since: '2026-09-29',
  },
  {
    id: 'edzesterv',
    title: 'Edzésterv',
    description: 'Az edzés blokkokba rendezett terve, gyakorlatokkal a gyakorlattárból vagy egy mentett sablonból.',
    module: 'trainings',
    to: '/events',
    audience: 'staff',
    since: '2026-09-29',
  },
  {
    id: 'edzes-visszajelzes',
    title: 'Edzés utáni visszajelzés',
    description: 'A játékosok az edzés után értékelhetik, milyen volt - az edző látja, mi működött.',
    module: 'trainings',
    to: '/events',
    since: '2026-10-05',
  },
  {
    id: 'gyakorlattar',
    title: 'Gyakorlattár rajztáblával',
    description: 'A stáb közös gyakorlatgyűjteménye kategóriákkal, eszközlistával és rajzolt ábrával.',
    module: 'drills',
    to: '/drills',
    since: '2026-09-29',
  },
  {
    id: 'edzessablonok',
    title: 'Edzéssablonok',
    description: 'Teljes edzéstervek menthetők és újrahasznosíthatók bármelyik edzésnél.',
    module: 'drills',
    to: '/drills',
    since: '2026-09-29',
  },
  {
    id: 'edzes-latogatottsag',
    title: 'Edzéslátogatottság',
    description: 'Ki hány edzésen volt, hányat hagyott ki, és mikor járt utoljára - százalékos részvétellel.',
    module: 'training-statistics',
    to: '/training-statistics',
    since: '2026-09-29',
  },
  {
    id: 'jelenleti-iv',
    title: 'Jelenléti ív Excelben',
    description: 'A rögzített jelenlét egy kattintással letölthető Excel táblázatként, pl. pályázati elszámoláshoz.',
    module: 'training-statistics',
    to: '/training-statistics',
    audience: 'staff',
    since: '2026-10-05',
  },

  // Utazás
  {
    id: 'utazasszervezes',
    title: 'Utazásszervezés',
    description: 'Ki melyik autóval vagy busszal megy, honnan és mikor indul - a szabad helyek száma élőben látszik.',
    module: 'travel',
    to: '/travel',
    since: '2026-10-02',
  },
  {
    id: 'jarmuvek',
    title: 'Klub járművei',
    description: 'A rendszeresen használt járművek egyszer felvehetők, és minden utazásnál kiválaszthatók.',
    module: 'travel',
    to: '/travel',
    audience: 'staff',
    since: '2026-10-02',
  },
  {
    id: 'fuvar-jelzes',
    title: 'Fuvar kérése és felajánlása',
    description: 'A szülő jelzi, hogy a gyerekének fuvar kell, maga megy, vagy melyik megállóban száll fel; aki autóval megy, felajánlhatja a szabad helyeit, és beültetheti a csapat gyerekeit.',
    module: 'travel',
    to: '/events',
    since: '2026-10-09',
  },
  {
    id: 'utazas-edzesre',
    title: 'Utazás edzésre és másolás',
    description: 'Az edzésekhez is szervezhető utazás (pl. klub kisbusz megállókkal), és egyszeri beállítás után átmásolható a következő edzésekre.',
    module: 'travel',
    to: '/events',
    audience: 'staff',
    since: '2026-10-09',
  },

  // AI asszisztens
  {
    id: 'ai-edzesterv',
    title: 'Edzésterv AI-val',
    description: 'Témából, időtartamból és létszámból kész edzésterv bemelegítéstől levezetésig, a klub gyakorlattárának felhasználásával.',
    module: 'trainings',
    to: '/events',
    audience: 'staff',
    since: '2026-10-21',
  },
  {
    id: 'ai-rajz',
    title: 'Gyakorlatrajz szövegből',
    description: 'A gyakorlat leírásából az AI megrajzolja az ábrát a rajztáblán: játékosok, bóják, passzok, futások.',
    module: 'drills',
    to: '/drills',
    since: '2026-10-21',
  },
  {
    id: 'ai-bejelentes',
    title: 'Bejelentés AI-val',
    description: 'Pár kulcsszóból kész, udvarias üzenőfal-bejelentés a választott hangnemben, az esemény időpontjával és helyszínével.',
    module: 'board',
    to: '/board',
    audience: 'staff',
    since: '2026-10-21',
  },
  {
    id: 'ai-beszamolo',
    title: 'Mérkőzés-beszámoló AI-val',
    description: 'Az eredményből és a gólszerzőkből beszámoló a szülőknek, a közösségi médiába vagy rövid összefoglalóként.',
    module: 'matches',
    to: '/matches',
    audience: 'staff',
    since: '2026-10-21',
  },
  {
    id: 'ai-kerdesek',
    title: 'Kérdezz az adatokról',
    description: 'Hétköznapi kérdésekre („Kinek jár le a sportorvosija?”) táblázatos válasz, a saját jogosultságaid szerint.',
    module: 'ai',
    to: '/ai',
    since: '2026-10-21',
  },
  {
    id: 'ai-heti-osszefoglalo',
    title: 'Heti edzői összefoglaló',
    description: 'Részvétel, terhelés, visszajelzések és távollétek alapján: kire figyelj, kit dicsérj, mit tervezz a jövő hétre.',
    module: 'ai',
    to: '/ai',
    since: '2026-10-21',
  },
  {
    id: 'ai-ertekeles',
    title: 'Játékos-értékelés AI-val',
    description: 'A statisztikákból és a megfigyeléseidből fejlődési értékelés vázlata a szülőknek vagy a stábnak.',
    module: 'players',
    to: '/players',
    since: '2026-10-21',
  },
  {
    id: 'ai-kitoltes',
    title: 'Kitöltés dokumentumból',
    description: 'A kitöltött jelentkezési lap fotójából vagy egy email szövegéből az AI kitölti az új játékos adatait.',
    module: 'players',
    to: '/players',
    audience: 'admin',
    since: '2026-10-21',
  },
  {
    id: 'ai-beallitasok',
    title: 'AI beállítások és modellválasztás',
    description: 'Az AI be- és kikapcsolása, modell választása (akár ingyenes Google Gemma), napi korlátok és felhasználási statisztika.',
    module: 'system-settings',
    to: '/system-settings',
    since: '2026-10-21',
  },

  // Üzenőfal, üzenetek
  {
    id: 'uzenofal',
    title: 'Üzenőfal',
    description: 'Gyors közlemények a klubnak, egy-egy korosztálynak vagy a vendégeknek - kitűzéssel, lejárattal, olvasottsággal.',
    module: 'board',
    to: '/board',
    since: '2026-09-05',
  },
  {
    id: 'uzenofal-esemenyhez',
    title: 'Bejegyzés eseményhez kötve',
    description: 'Egy üzenőfal-bejegyzés eseményhez köthető: az esemény oldalán is megjelenik, a bejegyzésből pedig egy kattintással az eseményhez jutsz.',
    module: 'board',
    to: '/board',
    since: '2026-10-09',
  },
  {
    id: 'uzenetek',
    title: 'Közvetlen üzenetek',
    description: 'Üzenetváltás szülő és edző között, olvasási visszaigazolással; az új üzenet azonnal felugrik, bármelyik oldalon vagy.',
    module: 'messages',
    to: '/messages',
    since: '2026-01-29',
  },

  // Játékosok
  {
    id: 'jatekos-nyilvantartas',
    title: 'Játékosnyilvántartás',
    description: 'Személyes adatok, korosztály, iskola, dokumentumok és távollétek játékosonként, névre, TAJ-ra vagy IFA kódra kereshetően.',
    module: 'players',
    to: '/players',
    since: '2026-01-29',
  },
  {
    id: 'jatekos-gyorsszurok',
    title: 'Gyorsszűrők és rendezés',
    description: 'Egy kattintással a lejárt vagy hamarosan lejáró sportorvosisok, az IFA-kód nélküliek vagy a betegek - darabszámmal; a lista oszloponként rendezhető, 800 játékosnál is gyors.',
    module: 'players',
    to: '/players',
    since: '2026-10-06',
  },
  {
    id: 'sportorvosi-csoportos',
    title: 'Sportorvosi ellenőrzés nagy létszámra',
    description: 'Fülek állapot szerint, keresés, „mind kijelölése”, közös dátum egy kattintással akár több száz játékosnak, és frissítés az IFA-ból - az érvényes igazolásúaknak is.',
    module: 'players',
    to: '/players',
    since: '2026-10-06',
  },
  {
    id: 'kapcsolattartok',
    title: 'Kapcsolattartók és egészségügyi tudnivalók',
    description: 'Szülők, vészhelyzeti kapcsolattartó, allergia és gyógyszer - a szülő maga is frissítheti a Profil oldalon.',
    module: 'players',
    to: '/players',
    since: '2026-10-05',
  },
  {
    id: 'excel-import-export',
    title: 'Excel import és export',
    description: 'Játékosok felvétele egy kitöltött Excel mintából, és a teljes névsor letöltése táblázatként.',
    module: 'players',
    to: '/players',
    since: '2026-10-05',
  },
  {
    id: 'sportorvosi-figyelmeztetes',
    title: 'Sportorvosi figyelmeztetés',
    description: 'Jelzés a lejárt és hamarosan lejáró sportorvosi igazolásokról a kezdőlapon, a névsorban és emailben.',
    module: 'players',
    to: '/players',
    since: '2026-09-07',
  },
  {
    id: 'tomeges-meghivo',
    title: 'Szülők tömeges meghívása',
    description: 'Egy lépésben meghívó küldhető a kijelölt játékosok szüleinek.',
    module: 'players',
    to: '/players',
    since: '2026-09-03',
  },
  {
    id: 'jatekos-inaktivalas',
    title: 'Játékos inaktiválása',
    description: 'A klubot elhagyó játékos inaktívvá tehető - az adatai és statisztikái megmaradnak, de a listákban nem zavar.',
    module: 'players',
    to: '/players',
    since: '2026-09-28',
  },
  {
    id: 'tavollet',
    title: 'Betegség, sérülés bejelentése',
    description: 'A szülő bejelentheti, ha a gyerek beteg vagy sérült; az edző látja, és a távollét idejére nem kell egyenként visszajelezni.',
    module: 'profile',
    to: '/profile',
    since: '2026-10-05',
  },

  // Jelentkezés, iskolák, korosztályok
  {
    id: 'nyilvanos-jelentkezes',
    title: 'Online jelentkezés új játékosoknak',
    description: 'Megosztható jelentkezési űrlap; a beérkező jelentkezést a stáb korosztályba sorolva fogadja el vagy utasítja el.',
    module: 'applications',
    to: '/applications',
    since: '2026-10-05',
  },
  {
    id: 'iskolak',
    title: 'Iskolák és kapcsolattartók',
    description: 'A játékosok iskolái, kapcsolattartóval - a Dokumentumok közül egy kikérő vagy igazolás közvetlenül elküldhető nekik emailben.',
    module: 'schools',
    to: '/schools',
    since: '2026-10-02',
  },
  {
    id: 'korosztalyok',
    title: 'Korosztályok kezelése',
    description: 'Csapatok születési év tartománnyal, edzőkkel, tömeges játékosfelvétellel és a nem korosztálybeli játékosok jelzésével.',
    module: 'age-groups',
    to: '/age-groups',
    since: '2026-08-05',
  },

  // Tagdíj, dokumentumok
  {
    id: 'tagdij',
    title: 'Tagdíj nyilvántartás',
    description: 'Havi tagdíjak automatikus generálása, befizetés jelölése, egyedi díj, kintlévőség és Excel export.',
    module: 'membership-fees',
    to: '/membership-fees',
    since: '2026-04-04',
  },
  {
    id: 'tagdij-generalas',
    title: 'Tagdíj generálás előnézettel',
    description: 'Hónap, kinek (egész klub, korosztályok vagy kiválasztott játékosok) és mennyi - előnézettel, hogy kinek mennyi készül. 0 Ft-os tagdíj nem jön létre.',
    module: 'membership-fees',
    to: '/membership-fees',
    audience: 'admin',
    since: '2026-10-09',
  },
  {
    id: 'tagdij-csoportos',
    title: 'Csoportos tagdíjjelölés',
    description: 'Korosztály-szűrő és kijelölés: egy csapat összes befizetése egy kattintással jelölhető, a lista nem ugrál és nem töltődik újra.',
    module: 'membership-fees',
    to: '/membership-fees',
    audience: 'staff',
    since: '2026-10-06',
  },
  {
    id: 'tagdij-emlekezteto',
    title: 'Tagdíj emlékeztető',
    description: 'Emlékeztető email a tartozóknak egyenként, mindenkinek egyszerre vagy havonta automatikusan.',
    module: 'membership-fees',
    to: '/membership-fees',
    audience: 'admin',
    since: '2026-10-05',
  },
  {
    id: 'dokumentumok',
    title: 'Dokumentumtár',
    description: 'Közös szabályzatok és nyomtatványok, valamint játékosokhoz csatolt igazolások egy helyen.',
    module: 'documents',
    to: '/documents',
    since: '2026-07-24',
  },

  // Pályák
  {
    id: 'palyak',
    title: 'Pályák és pályarészek',
    description: 'A klub pályái kapacitással, és hogy hány részre oszthatók.',
    module: 'venues',
    to: '/venues',
    since: '2026-04-06',
  },
  {
    id: 'palyareszek-neve',
    title: 'Elnevezett pályarészek',
    description: 'A pálya részei elnevezhetők (pl. „Kapu felőli”, „Öltöző felőli”): a foglalásnál, a foglaltságnál és az eseményeknél a nevük látszik.',
    module: 'venues',
    to: '/venues',
    since: '2026-10-11',
  },
  {
    id: 'foglaltsag',
    title: 'Pályafoglaltság',
    description: 'Heti és napi rács pályarészenként: egy pillantásra látszik, mikor szabad a pálya.',
    module: 'venue-occupancy',
    to: '/venue-occupancy',
    since: '2026-04-06',
  },
  {
    id: 'vendeg-foglalas',
    title: 'Pályafoglalás vendégeknek',
    description: 'A gondnok, a takarító vagy egy külsős csapat pályát kérhet, amit az admin jóváhagy vagy indoklással elutasít.',
    module: 'bookings',
    to: '/bookings',
    since: '2026-09-06',
  },

  // Rendszer
  {
    id: 'meghivok',
    title: 'Meghívásos regisztráció',
    description: 'Új fiók csak meghívóval jön létre: a meghívott a linken maga állítja be a jelszavát.',
    module: 'users',
    to: '/users',
    since: '2026-08-25',
  },
  {
    id: 'felhasznalo-kereses',
    title: 'Felhasználók keresése',
    description: 'Név vagy email szerinti keresés és szerepkör-szűrő; a szülőhöz a gyerekek gépeléssel választhatók ki a teljes névsor helyett.',
    module: 'users',
    to: '/users',
    since: '2026-10-06',
  },
  {
    id: 'tobb-szerepkor',
    title: 'Több szerepkör egy fiókban',
    description: 'Valaki lehet egyszerre edző és szülő is - mindkét szerepben mindent lát, amit kell.',
    module: 'users',
    to: '/users',
    since: '2026-09-03',
  },
  {
    id: 'vendeg-szerepkor',
    title: 'Vendég szerepkör',
    description: 'Gondnok, kitmanager vagy külsős csapat: csak a naptárt, a saját foglalásait és a nekik szóló bejegyzéseket látják.',
    module: 'users',
    to: '/users',
    since: '2026-09-06',
  },
  {
    id: 'onregisztracio',
    title: 'Önregisztráció jóváhagyással',
    description: 'Aki magától regisztrál, jóváhagyásra vár; az admin szerepkört ad neki és összeköti a játékossal.',
    module: 'registrations',
    to: '/registrations',
    since: '2026-09-03',
  },
  {
    id: 'email-naplo',
    title: 'Email napló',
    description: 'Minden kiküldött levél nyoma: ha valaki azt mondja, nem kapott emailt, itt kiderül.',
    module: 'email-log',
    to: '/email-log',
    since: '2026-09-03',
  },
  {
    id: 'ifa-import',
    title: 'MLSZ IFA kapcsolat',
    description: 'Játékosok importálása az IFA-ból, helyben felvett játékos összekötése, sportorvosi dátumok frissítése.',
    module: 'ifa',
    to: '/ifa',
    since: '2026-09-07',
  },
  {
    id: 'ifa-mentett-belepes',
    title: 'IFA belépés a Profilból',
    description: 'A Profilban mentett IFA belépéssel az IFA oldal egy gombnyomással lekérdez - nem kell újra beírni; az itt beírt belépés el is menthető.',
    module: 'ifa',
    to: '/ifa',
    since: '2026-10-06',
  },
  {
    id: 'arculat',
    title: 'Klub arculata',
    description: 'Klubnév, címer és szín - a címer a telefonra telepített app ikonja és az értesítések képe is.',
    module: 'club-settings',
    to: '/club-settings',
    since: '2026-04-04',
  },
  {
    id: 'alap-emlekeztetok',
    title: 'Alapértelmezett emlékeztetők',
    description: 'Minden új eseményhez automatikusan beállított emlékeztetők, amiket az edző eseményenként felülírhat.',
    module: 'club-settings',
    to: '/club-settings',
    since: '2026-09-28',
  },
  {
    id: 'modul-kapcsolok',
    title: 'Modulok ki- és bekapcsolása',
    description: 'Tagdíjkezelés, nyilvános jelentkezés, vendégfoglalás, edzés utáni visszajelzés - a klub azt kapcsolja be, amit használ.',
    module: 'system-settings',
    to: '/system-settings',
    since: '2026-09-03',
  },
  {
    id: 'uzenofal-jogok',
    title: 'Üzenőfal jogosultságok',
    description: 'Beállítható, ki tehet közzé bejegyzést és ki szólhat hozzá.',
    module: 'system-settings',
    to: '/system-settings',
    since: '2026-09-28',
  },
  {
    id: 'sportorvosi-ablak',
    title: 'Állítható sportorvosi figyelmeztetés',
    description: 'A klub maga dönti el, hány nappal a lejárat előtt jelezzen a rendszer (30, 60, 90 nap vagy egyéni).',
    module: 'system-settings',
    to: '/system-settings',
    since: '2026-10-05',
  },

  // Fiók
  {
    id: 'push-ertesitesek',
    title: 'Push értesítések',
    description: 'Értesítés a telefonra vagy a gépre új eseményről, emlékeztetőről, üzenőfal bejegyzésről - bármikor kipróbálható.',
    module: 'profile',
    to: '/profile',
    since: '2026-08-24',
  },
  {
    id: 'email-beallitasok',
    title: 'Választható email értesítések',
    description: 'Heti összefoglaló, sportorvosi emlékeztető, visszajelzés-változás - mindenki maga dönti el, mit kér.',
    module: 'profile',
    to: '/profile',
    since: '2026-10-05',
  },
  {
    id: 'telepitheto-app',
    title: 'Telepíthető mobil app',
    description: 'A kezdőképernyőre tehető, telefonra szabott felület alsó menüvel - külön alkalmazásbolt nélkül.',
    module: 'profile',
    to: '/profile',
    since: '2026-07-24',
  },
];

/** A menü csoportjai, hogy a funkciók ott legyenek, ahol a menüben is keresnéd őket. */
export const FEATURE_GROUPS: { title: string; modules: string[] }[] = [
  { title: 'Általános', modules: ['dashboard', 'profile'] },
  { title: 'Szülőknek és játékosoknak', modules: ['family-home', 'family-program', 'family-me'] },
  {
    title: 'Sport',
    modules: ['calendar', 'events', 'matches', 'statistics', 'trainings', 'drills', 'training-statistics', 'travel', 'ai'],
  },
  {
    title: 'Klub kezelése',
    modules: ['board', 'messages', 'players', 'applications', 'schools', 'age-groups', 'membership-fees', 'documents'],
  },
  { title: 'Pályák', modules: ['venues', 'venue-occupancy', 'bookings'] },
  {
    title: 'Rendszer',
    modules: ['users', 'registrations', 'email-log', 'ifa', 'club-settings', 'system-settings'],
  },
];

/** Hány napig számít újnak egy funkció. */
export const NEW_FEATURE_DAYS = 30;

export const isNewFeature = (feature: Feature, now: Date = new Date()) =>
  now.getTime() - new Date(feature.since).getTime() < NEW_FEATURE_DAYS * 24 * 60 * 60 * 1000;
